import { computed, onBeforeUnmount, onMounted, ref, watch, type Ref } from 'vue';

/**
 * Windowed rendering for a uniform square-cell grid (emoji, icon pickers): only the rows near
 * the viewport are mounted, so a 1 870-cell grid keeps a few dozen nodes instead of thousands.
 *
 * The grid scrolls with its nearest scrollable ancestor, so it can sit inside an existing panel.
 * Render a wrapper with `height: totalHeight` and, inside it, the cells `[start, end)` shifted
 * down by `offsetY`.
 */
export interface VirtualGridOptions {
  /** Element the cells are laid out in (its width decides the cell size). */
  host: Ref<HTMLElement | null>;
  count: Ref<number>;
  columns: number;
  /** Gap between cells in px (row gap = column gap). */
  gap: number;
  /** Extra rows mounted above and below the viewport. */
  overscanRows?: number;
}

function scrollParent(node: HTMLElement | null): HTMLElement | null {
  let current = node?.parentElement ?? null;
  while (current) {
    const overflow = getComputedStyle(current).overflowY;
    if (overflow === 'auto' || overflow === 'scroll') return current;
    current = current.parentElement;
  }
  return null;
}

export function useVirtualGrid(options: VirtualGridOptions) {
  const overscan = options.overscanRows ?? 4;
  const width = ref(0);
  const viewTop = ref(0);
  const viewHeight = ref(typeof window === 'undefined' ? 800 : window.innerHeight);
  let scroller: HTMLElement | null = null;
  let observer: ResizeObserver | null = null;

  const rowHeight = computed(() => {
    const cell = (width.value - options.gap * (options.columns - 1)) / options.columns;
    return Math.max(1, cell + options.gap);
  });
  const rows = computed(() => Math.ceil(options.count.value / options.columns));
  const totalHeight = computed(() => Math.max(0, rows.value * rowHeight.value - options.gap));
  const startRow = computed(() => Math.max(0, Math.floor(viewTop.value / rowHeight.value) - overscan));
  const endRow = computed(() => Math.min(rows.value, Math.ceil((viewTop.value + viewHeight.value) / rowHeight.value) + overscan));
  const start = computed(() => startRow.value * options.columns);
  const end = computed(() => Math.min(options.count.value, endRow.value * options.columns));
  const offsetY = computed(() => startRow.value * rowHeight.value);

  function measure() {
    const host = options.host.value;
    if (!host) return;
    width.value = host.clientWidth;
    const hostTop = host.getBoundingClientRect().top;
    if (scroller) {
      const box = scroller.getBoundingClientRect();
      viewTop.value = Math.max(0, box.top - hostTop);
      viewHeight.value = scroller.clientHeight;
    } else {
      viewTop.value = Math.max(0, -hostTop);
      viewHeight.value = window.innerHeight;
    }
  }

  function attach() {
    detach();
    const host = options.host.value;
    if (!host) return;
    scroller = scrollParent(host);
    (scroller ?? window).addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure, { passive: true });
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(measure);
      observer.observe(host);
      if (scroller) observer.observe(scroller);
    }
    measure();
  }

  function detach() {
    (scroller ?? window).removeEventListener('scroll', measure);
    window.removeEventListener('resize', measure);
    observer?.disconnect();
    observer = null;
    scroller = null;
  }

  onMounted(attach);
  watch(options.host, attach);
  // A new filter changes the height; re-measure so the window is right before the next scroll.
  watch(options.count, () => queueMicrotask(measure));
  onBeforeUnmount(detach);

  return { start, end, offsetY, totalHeight, measure };
}
